import { api } from './api';

export interface SharedQuoteSummary {
  readonly shareToken: string;
  readonly title: string;
  readonly customerName: string | null;
  readonly customerEmail: string | null;
  readonly status: string;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly viewedAt: string | null;
  readonly orderedAt: string | null;
}

export interface SharedQuoteDetail extends SharedQuoteSummary {
  readonly message: string | null;
  readonly fileName: string;
  readonly volumeCm3: number;
  readonly surfaceAreaCm2: number;
  readonly boundingBox: { width: number; height: number; depth: number };
  readonly prices: readonly {
    materialId: string;
    price: number;
    rawPrice: number;
    estimatedDays: number;
  }[];
  readonly orderId: string | null;
}

export interface CreateSharedQuoteInput {
  readonly title: string;
  readonly customerName?: string | null;
  readonly customerEmail?: string | null;
  readonly message?: string | null;
  readonly fileKey: string;
  readonly fileName: string;
  readonly volumeCm3: number;
  readonly surfaceAreaCm2: number;
  readonly boundingBox: { width: number; height: number; depth: number };
  readonly expiresInDays?: number;
}

export const sharedQuotesApi = {
  create: async (input: CreateSharedQuoteInput): Promise<SharedQuoteDetail> => {
    const result = await api.post<SharedQuoteDetail>('/admin/shared-quotes', input);
    return result.data!;
  },

  list: async (
    params?: { status?: string; lastKey?: string },
  ): Promise<{
    data: readonly SharedQuoteSummary[];
    pagination: { lastKey: string | null };
  }> => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.lastKey) query.set('lastKey', params.lastKey);
    const qs = query.toString();
    const result = await api.get<readonly SharedQuoteSummary[]>(
      `/admin/shared-quotes${qs ? `?${qs}` : ''}`,
    );
    return {
      data: result.data ?? [],
      pagination: result.pagination ?? { lastKey: null },
    };
  },

  get: async (token: string): Promise<SharedQuoteDetail> => {
    const result = await api.get<SharedQuoteDetail>(`/admin/shared-quotes/${token}`);
    return result.data!;
  },

  delete: async (token: string): Promise<void> => {
    await api.delete(`/admin/shared-quotes/${token}`);
  },

  sendEmail: async (token: string, to: string, customMessage?: string): Promise<void> => {
    await api.post(`/admin/shared-quotes/${token}/send`, { to, customMessage });
  },
};
